/**
 * 邀请推广页：邀请链接、汇总指标、邀请记录与返利明细（tab 切换）。
 */

import { useEffect, useState } from 'react';
import { AlertCircle, Check, Copy, Loader2, Users } from 'lucide-react';
import {
  invitationAPI,
  type InvitationInfo,
  type MyReferralRebateRecord,
  type PaginationInfo,
} from '../../services/api';
import { formatBeijingDateTime } from '../../utils/time';
import { EmptyState, Metric, SectionCard } from './shared';

type TabKey = 'invites' | 'rebates';

const REBATE_STATUS_LABEL: Record<string, string> = {
  credited: '已到账',
  reversed: '已冲销',
  blocked: '未发放',
};

export default function InvitePage() {
  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [rebates, setRebates] = useState<MyReferralRebateRecord[]>([]);
  const [rebatePagination, setRebatePagination] = useState<PaginationInfo>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [tab, setTab] = useState<TabKey>('invites');
  const [loading, setLoading] = useState(true);
  const [rebateLoading, setRebateLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const loadRebates = async (page: number, append: boolean) => {
    setRebateLoading(true);
    try {
      const res = await invitationAPI.getMyRebates(page, 10);
      setRebates((prev) => (append ? [...prev, ...res.data] : res.data));
      setRebatePagination(res.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载返利明细失败');
    } finally {
      setRebateLoading(false);
    }
  };

  useEffect(() => {
    invitationAPI
      .getMyInvitations()
      .then((res) => {
        setInfo(res);
        if (res.rebate?.enabled) {
          void loadRebates(1, false);
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : '加载邀请信息失败'))
      .finally(() => setLoading(false));
  }, []);

  const handleCopy = async () => {
    if (!info?.inviteLink) return;
    try {
      await navigator.clipboard.writeText(info.inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('复制失败，请手动选择链接复制');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (!info || (!info.inviteEnabled && !info.rebate?.enabled)) {
    return (
      <SectionCard title="邀请推广" bodyClassName="p-10">
        <EmptyState text="邀请功能暂未开放" />
      </SectionCard>
    );
  }

  const rebate = info.rebate;
  const rebateNet = rebate ? rebate.totalCredits - rebate.totalReversed : 0;

  return (
    <>
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      <section className="bg-white border border-light-border rounded-2xl shadow-sm p-5">
        <div className={`grid gap-3 ${rebate?.enabled ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <Metric label="已邀请" value={`${info.totalInvited} 人`} />
          <Metric label="注册奖励" value={`+${info.totalEarned}`} tone="earn" />
          {rebate?.enabled && (
            <Metric label="累计返利" value={`+${rebateNet}`} tone="earn" />
          )}
        </div>
      </section>

      <SectionCard title="我的邀请链接" bodyClassName="p-5">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 min-w-0 bg-light-bg border border-light-border rounded-xl px-3 py-2.5 text-xs sm:text-sm text-light-text font-mono truncate">
            {info.inviteLink}
          </div>
          <button
            onClick={handleCopy}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-accent-blue text-white rounded-xl text-sm hover:opacity-90 transition-opacity shrink-0"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? '已复制' : '复制链接'}
          </button>
        </div>
        <p className="text-xs text-light-muted mt-3 leading-relaxed">
          {info.inviteEnabled && (
            <>好友通过链接注册，你得 {info.inviterCredits} 积分、好友得 {info.inviteeCredits} 积分。</>
          )}
          {rebate?.enabled && (
            <>
              好友每次充值，你再得 {rebate.creditsPerYuan} 积分/元（按实付金额返 {rebate.percent}%）
              {rebate.minOrderAmount > 0 ? `，单笔满 ¥${rebate.minOrderAmount} 起返` : ''}。
            </>
          )}
        </p>
      </SectionCard>

      <SectionCard bodyClassName="p-0">
        <div className="px-5 pt-4 border-b border-light-border flex gap-4">
          <button
            onClick={() => setTab('invites')}
            className={`pb-3 text-sm transition-colors border-b-2 -mb-px ${
              tab === 'invites'
                ? 'border-accent-blue text-accent-blue font-medium'
                : 'border-transparent text-light-secondary hover:text-light-text'
            }`}
          >
            邀请记录
          </button>
          {rebate?.enabled && (
            <button
              onClick={() => setTab('rebates')}
              className={`pb-3 text-sm transition-colors border-b-2 -mb-px ${
                tab === 'rebates'
                  ? 'border-accent-blue text-accent-blue font-medium'
                  : 'border-transparent text-light-secondary hover:text-light-text'
              }`}
            >
              返利明细
            </button>
          )}
        </div>

        {tab === 'invites' ? (
          info.invitations.length === 0 ? (
            <EmptyState text="还没有好友通过你的链接注册" />
          ) : (
            <div className="divide-y divide-light-border">
              {info.invitations.map((inv) => (
                <div key={inv.id} className="px-5 py-3.5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-light-hover flex items-center justify-center shrink-0">
                    <Users className="w-4 h-4 text-light-secondary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-light-text truncate">
                      {inv.inviteeName || inv.inviteeEmail}
                    </p>
                    <p className="text-xs text-light-muted mt-0.5">
                      {formatBeijingDateTime(inv.createdAt)}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-green-600 tabular-nums shrink-0">
                    +{inv.inviterBonus}
                  </span>
                </div>
              ))}
            </div>
          )
        ) : rebates.length === 0 ? (
          <EmptyState text={rebateLoading ? '加载中...' : '好友充值后，返利会自动到账'} />
        ) : (
          <>
            <div className="divide-y divide-light-border">
              {rebates.map((item) => {
                const isCredited = item.status === 'credited';
                const isReversed = item.status === 'reversed';
                return (
                  <div key={item.id} className="px-5 py-3.5 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-light-hover flex items-center justify-center shrink-0">
                      <Users className="w-4 h-4 text-light-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-light-text truncate">
                        {item.inviteeName || item.inviteeEmail}
                      </p>
                      <p className="text-xs text-light-muted mt-0.5">
                        {formatBeijingDateTime(item.createdAt)} · 实付 ¥
                        {item.baseAmount.toFixed(2)} · {REBATE_STATUS_LABEL[item.status] ?? item.status}
                      </p>
                    </div>
                    <span
                      className={`text-sm font-semibold tabular-nums shrink-0 ${
                        isCredited ? 'text-green-600' : 'text-light-muted'
                      }`}
                    >
                      {isCredited
                        ? `+${item.credits}`
                        : isReversed
                          ? `−${item.reversedCredits}`
                          : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
            {rebatePagination.page < rebatePagination.totalPages && (
              <div className="px-5 py-3 border-t border-light-border text-center">
                <button
                  onClick={() => void loadRebates(rebatePagination.page + 1, true)}
                  disabled={rebateLoading}
                  className="text-xs text-accent-blue hover:underline disabled:opacity-50"
                >
                  {rebateLoading ? '加载中...' : '加载更多'}
                </button>
              </div>
            )}
          </>
        )}
      </SectionCard>
    </>
  );
}
